import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMostRecentLoc } from 'emergency_data/src/utils/getMostRecentLoc';
import { mapStyles } from '@rsos/components/capstone/common/Map/mapStyles';
import {
  BASEMAP_ESRI_SATELLITE,
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { setGoogleMapZoomTo } from '@rsos/utils/panMapToCoordinate';

/**
 * Google basemap transition handling
 * @param {Object} mapInstance - The Google map instance object.
 */
const useGoogleMapTransition = mapInstance => {
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const theme = useSelector(state => state.sinatra.ui.theme);

  const selectedCallerID = useSelector(
    state => state?.emergencyCalls?.selectedCallerID,
  );

  const selectedAlertID = useSelector(
    state => state?.incidents?.selectedAlertID,
  );

  const callQueue = useSelector(state =>
    state?.emergencyCalls?.callQueue
      ? callQueueSelector(state.emergencyCalls.callQueue)
      : {},
  );

  const hasLocation = !!getMostRecentLoc(callQueue?.[selectedCallerID]);

  const isAllView = useMemo(() => {
    return (!selectedCallerID || !hasLocation) && !selectedAlertID;
  }, [hasLocation, selectedAlertID, selectedCallerID]);

  // Update the Google basemap between 'roadmap' and 'hybrid'.
  useEffect(() => {
    if (basemap && mapInstance && mapProvider === GOOGLE) {
      mapInstance.setMapTypeId(
        basemap === BASEMAP_GOOGLE_ROADMAP
          ? BASEMAP_GOOGLE_ROADMAP
          : BASEMAP_GOOGLE_HYBRID,
      );
    }
  }, [basemap, mapInstance, mapProvider]);

  // Update Map with the associated theme's Map styling
  useEffect(() => {
    if (mapInstance && mapProvider === GOOGLE && theme) {
      mapInstance.setOptions({
        styles: mapStyles[theme],
      });
    }
  }, [mapInstance, mapProvider, theme]);

  // When the mapProvider is switched to Google via the basemap menu,
  // update the map bounds to the specific view state where the user was on Esri map.
  useEffect(() => {
    if (
      mapInstance &&
      mapProvider === GOOGLE &&
      window?.__RSOS?.mapViewState &&
      isAllView
    ) {
      const { bounds, initialEsriBasemap, zoom } = window.__RSOS.mapViewState;

      window.google.maps.event.addListenerOnce(
        mapInstance,
        'bounds_changed', // https://developers.google.com/maps/documentation/javascript/events
        () => {
          mapInstance.fitBounds(bounds);

          setGoogleMapZoomTo({
            mapInstance,
            zoom:
              initialEsriBasemap === BASEMAP_ESRI_SATELLITE ? zoom : zoom + 1,
          });
        },
      );

      delete window.__RSOS.mapViewState;
    }
  }, [isAllView, mapInstance, mapProvider]);
};

export default useGoogleMapTransition;
